/**
 * A server's LAN address in the site's server list (cleanup wave G): the address in effect and where it comes from —
 * auto, chosen here, or set on the server (`ACP_LAN_IP`, which wins) — and "Change", which opens the choices: auto,
 * each address the node reported on its host (with the warning `lanIpProblem` gives it: Docker's bridge, Docker
 * Desktop's VM, a VM's NAT adapter, link-local), or another private address typed in (a NATed host's). The choice
 * goes to the node over its site link; its DNS name and its pair follow once it reports the new address.
 *
 * Presentational: the node's fields come in (`lib/admin/lanAddress.ts` turns them into choices); the choice goes out
 * as `onSetLanIp(address | null)` (null: auto). Whether the chooser is open is this component's own state.
 */
import type { SiteNodeWire } from '@norna/api-contract/sites.js';
import { TriangleAlertIcon } from 'lucide-react';
import { useState } from 'react';
import { initialChoice, isChoosableLanIp, lanOptions, lanSourceText } from '../../lib/admin/lanAddress.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';

export type LanAddressControlProps = {
  readonly node: Pick<SiteNodeWire, 'nodeId' | 'lanIp' | 'lanIpSetting' | 'lanCandidates' | 'lanIpSource'>;
  readonly pending: boolean;
  readonly onSetLanIp: (lanIp: string | null) => void;
  /** The chooser open on first render (stories and specs); otherwise the operator's "Change". */
  readonly defaultOpen?: boolean;
};

type Choice = { readonly kind: 'option'; readonly value: string | null } | { readonly kind: 'other'; readonly text: string };

function chosenValue(choice: Choice): string | null {
  return choice.kind === 'option' ? choice.value : choice.text.trim();
}

function Chooser({ node, pending, onSave, onCancel }: { readonly node: LanAddressControlProps['node']; readonly pending: boolean; readonly onSave: (lanIp: string | null) => void; readonly onCancel: () => void }) {
  const [choice, setChoice] = useState<Choice>(() => initialChoice(node));
  const options = lanOptions(node);
  const otherValid = choice.kind !== 'other' || isChoosableLanIp(choice.text);
  const unchanged = chosenValue(choice) === (node.lanIpSetting ?? null);
  const name = `lan-${node.nodeId}`;
  if (node.lanCandidates === undefined) {
    return (
      <fieldset className="mt-1 grid max-w-sm gap-1.5 rounded-md border p-2 text-xs" data-testid="lan-chooser">
        <legend className="px-1 font-medium">LAN address</legend>
        <p className="text-muted-foreground" data-testid="lan-no-candidates">
          This server has not reported its addresses — its release predates the portal’s LAN setting, or it has not connected yet — so it cannot take one from here. Update it, or set ACP_LAN_IP on its container.
        </p>
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid="lan-cancel">
            Close
          </Button>
        </div>
      </fieldset>
    );
  }
  return (
    <fieldset className="mt-1 grid max-w-sm gap-1.5 rounded-md border p-2 text-xs" data-testid="lan-chooser">
      <legend className="px-1 font-medium">LAN address</legend>
      {node.lanIpSource === 'environment' ? (
        <p className="text-muted-foreground" data-testid="lan-env-note">
          ACP_LAN_IP is set on this server: it wins until it is removed from the container. A choice here applies then.
        </p>
      ) : null}
      {options.map((o) => (
        <label key={o.value ?? 'auto'} className="flex items-start gap-2" data-testid="lan-option" data-value={o.value ?? 'auto'}>
          <input type="radio" name={name} className="mt-0.5" disabled={!o.choosable} checked={choice.kind === 'option' && choice.value === o.value} onChange={() => setChoice({ kind: 'option', value: o.value })} />
          <span className="flex flex-col">
            <span>
              <span className={o.value === null ? '' : 'font-mono'}>{o.label}</span>
              {o.interface === null ? null : <span className="text-muted-foreground"> · {o.interface}</span>}
              {o.inUse ? <span className="text-muted-foreground"> · in use</span> : null}
            </span>
            {o.warning === null ? null : (
              <span className="flex items-center gap-1 text-warning" data-testid="lan-option-warning">
                <TriangleAlertIcon className="size-3" aria-hidden="true" />
                {o.warning}
              </span>
            )}
          </span>
        </label>
      ))}
      <label className="flex items-center gap-2" data-testid="lan-option" data-value="other">
        <input type="radio" name={name} checked={choice.kind === 'other'} onChange={() => setChoice({ kind: 'other', text: choice.kind === 'other' ? choice.text : '' })} />
        <span>Another address</span>
      </label>
      {choice.kind === 'other' ? (
        <div className="grid gap-1 pl-5">
          <Input
            aria-label="LAN address"
            placeholder="192.168.1.40"
            className="h-7 font-mono text-xs"
            value={choice.text}
            onChange={(e) => setChoice({ kind: 'other', text: e.target.value })}
            data-testid="lan-other-input"
          />
          {choice.text !== '' && !otherValid ? (
            <span className="text-destructive" data-testid="lan-other-invalid">
              A private IPv4 address (10.x, 172.16–31.x, 192.168.x or 100.64–127.x): it is published in DNS under our name.
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="flex justify-end gap-1 pt-1">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="lan-cancel">
          Cancel
        </Button>
        <Button size="sm" disabled={pending || unchanged || !otherValid || (choice.kind === 'other' && choice.text.trim() === '')} onClick={() => onSave(chosenValue(choice))} data-testid="lan-save">
          Save
        </Button>
      </div>
    </fieldset>
  );
}

export function LanAddressControl({ node, pending, onSetLanIp, defaultOpen }: LanAddressControlProps) {
  const [open, setOpen] = useState(defaultOpen === true);
  return (
    <div className="mt-1 text-xs" data-testid="lan-address" data-source={node.lanIpSource ?? 'unreported'}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-muted-foreground">LAN address</span>
        <span className="font-mono">{node.lanIp ?? '–'}</span>
        <Badge variant={node.lanIpSource === 'environment' ? 'warning' : 'outline'} data-testid="lan-source">
          {lanSourceText(node)}
        </Badge>
        {open ? null : (
          <Button size="sm" variant="ghost" className="h-6 px-2" disabled={pending} onClick={() => setOpen(true)} data-testid="lan-change">
            Change
          </Button>
        )}
      </div>
      {open ? (
        <Chooser
          node={node}
          pending={pending}
          onCancel={() => setOpen(false)}
          onSave={(lanIp) => {
            onSetLanIp(lanIp);
            setOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
